<script setup lang="ts">
import { computed, useAttrs } from 'vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import { objectWithoutKeys } from '@/helpers'
import DropdownMenu from '@/components/dropdown-menu.vue'
import BaseButton from './base-button.vue'
import { type BaseButtonProps, BaseButtonDefaults } from './_constants'

defineOptions({ inheritAttrs: false })

type ButtonHighlightedProps = BaseButtonProps & {
  menuAlign?: 'left' | 'right'
  menuIcon?: IconDefinition
}

const props = withDefaults(defineProps<ButtonHighlightedProps>(), {
  ...BaseButtonDefaults,
  shadow: false,
  menuAlign: 'right',
})

const attrs = useAttrs()
const baseButtonProps = computed(() => objectWithoutKeys(props, ['menuAlign', 'menuIcon']))
const bindToBaseButton = computed(() => ({ ...attrs, ...baseButtonProps.value }))
</script>

<template>
  <DropdownMenu v-if="$slots.menu" :align="menuAlign" :class="$style.dropdownWrap">
    <BaseButton class="btn-highlighted" :class="$style['button-content']" v-bind="bindToBaseButton">
      <slot />
      <span :class="$style.btnDivider" aria-hidden="true"></span>
      <span :class="$style.caret" aria-hidden="true">
        <FontAwesomeIcon v-if="menuIcon" :icon="menuIcon" />
        <template v-else>▾</template>
      </span>
    </BaseButton>
    <template #items>
      <slot name="menu" />
    </template>
  </DropdownMenu>

  <BaseButton v-else class="btn-highlighted" v-bind="bindToBaseButton">
    <slot />
  </BaseButton>
</template>

<style module>
.button-content {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}
.btnDivider {
  width: 1px;
  height: 16px;
  background: currentColor;
  display: inline-block;
}
.caret {
  line-height: 1;
  display: inline-flex;
  align-items: center;
}
.caret :global(svg) {
  width: 12px;
  height: 12px;
}
.dropdownWrap {
  display: inline-flex;
}

.dropdownWrap :global(.dropdown-menu) {
  z-index: 2000;
}
</style>
